/**
 * Auth API Client
 *
 * Frontend client for Better Auth authentication endpoints.
 * Uses fetch with credentials: 'include' for cookie-based sessions.
 */

import { resolveApiBaseUrl, getApiBase } from './fetch';

/** Timeout for auth fetches so the UI doesn't hang when the backend isn't up yet. */
const AUTH_FETCH_TIMEOUT_MS = 10_000;

/** Retry config for OAuth URL fetch — handles API cold-start 5xx/network errors. */
const OAUTH_MAX_RETRIES = 2; // 3 total attempts (attempt 0, 1, 2)
const OAUTH_RETRY_DELAYS_MS = [2000, 4000, 8000] as const;

/** Returns true if an error is a network-level failure (no HTTP response). */
function isFetchNetworkError(err: unknown): boolean {
  return err instanceof TypeError && typeof err.message === 'string' && err.message.toLowerCase().includes('fetch');
}

function getRootApiUrl(): string {
  const base = resolveApiBaseUrl();
  const origin = window.location.origin;

  if (!base) return origin;

  // In dev, prefer same-origin for auth endpoints to keep cookies working.
  // This lets the Vite proxy handle /api -> localhost:3501 even when:
  //   - Both are localhost on different ports
  //   - The origin is a tunnel (ngrok) proxying to localhost
  if (import.meta.env.DEV) {
    try {
      const baseUrl = new URL(base);
      const originUrl = new URL(origin);
      const isLocalhost =
        baseUrl.hostname === originUrl.hostname ||
        (baseUrl.hostname === '127.0.0.1' && originUrl.hostname === 'localhost') ||
        (baseUrl.hostname === 'localhost' && originUrl.hostname === '127.0.0.1');

      const baseIsLocal = baseUrl.hostname === 'localhost' || baseUrl.hostname === '127.0.0.1';
      const originIsRemote = originUrl.hostname !== 'localhost' && originUrl.hostname !== '127.0.0.1';

      if ((isLocalhost && baseUrl.port !== originUrl.port) || (baseIsLocal && originIsRemote)) {
        return origin;
      }
    } catch {
      // If base isn't a valid URL, fall back to the configured base.
    }
  }

  return base;
}

// ==================== Types ====================

export interface DevConnectResult {
  apiKey: string;
  userId: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
}

export interface RegisterResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
  };
}

export interface StatsigBootstrapData {
  gates: Record<string, boolean>;
  experiments: Record<string, Record<string, unknown>>;
  generatedAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  subscriptionTier: string;
  createdAt: string;
  emailVerified?: boolean;
  statsig?: StatsigBootstrapData;
}

export interface UpdateUserRequest {
  name?: string;
  image?: string | null;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface Session {
  user: User;
  session: {
    id: string;
    userId: string;
    expiresAt: string;
  };
}

// ==================== Dev Connect ====================

/**
 * Dev-only: create a test session for local development.
 * Impersonate is disabled when NODE_ENV=production on the API.
 */
export async function devConnect(email?: string): Promise<DevConnectResult> {
  const root = getRootApiUrl();
  const impersonateUrl = `${root}/api/auth/test/impersonate`;
  const body = email ? { email } : { email: 'web-dev@local' };

  const res1 = await fetch(impersonateUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!res1.ok) {
    const err = await res1.json().catch(() => ({}));
    throw new Error(err.message || `Impersonate failed: ${res1.status}`);
  }

  const { userId } = await res1.json();

  // Create an API key using the session
  const apiBase = getApiBase();
  const createKeyUrl = `${apiBase}/users/me/api-keys`;
  const res2 = await fetch(createKeyUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ name: 'Web Dev', scopes: ['read', 'write'] }),
  });

  if (!res2.ok) {
    const err = await res2.json().catch(() => ({}));
    throw new Error(err.message || `Create API key failed: ${res2.status}`);
  }

  const data = await res2.json();
  const apiKey = data.apiKey;
  if (!apiKey) throw new Error('No apiKey in create key response');

  localStorage.setItem('danny_api_key', apiKey);
  return { apiKey, userId: userId ?? 'web-dev' };
}

// ==================== Email/Password Auth ====================

/**
 * Register new user with email/password
 * Better Auth endpoint: POST /api/auth/sign-up/email
 */
export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  const root = getRootApiUrl();
  const res = await fetch(`${root}/api/auth/sign-up/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email: data.email,
      password: data.password,
      name: data.name || data.email.split('@')[0],
    }),
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    // Better Auth returns { message: "..." } on error
    throw new Error(err.message || err.error || 'Registration failed');
  }

  return res.json();
}

/**
 * Login with email/password
 * Better Auth endpoint: POST /api/auth/sign-in/email
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  const root = getRootApiUrl();
  const loginUrl = `${root}/api/auth/sign-in/email`;
  const res = await fetch(loginUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email: data.email,
      password: data.password,
    }),
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || 'Login failed');
  }

  return res.json();
}

/**
 * Logout (clears auth session)
 * Better Auth endpoint: POST /api/auth/sign-out
 */
export async function logout(): Promise<void> {
  const root = getRootApiUrl();
  await fetch(`${root}/api/auth/sign-out`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: '{}',
  });

  // Also clear localStorage API key (dev mode)
  localStorage.removeItem('danny_api_key');
}

/**
 * Request password reset email
 * Better Auth endpoint: POST /api/auth/forgot-password
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const root = getRootApiUrl();
  const redirectTo = `${window.location.origin}/reset-password`;
  const res = await fetch(`${root}/api/auth/request-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, redirectTo }),
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  // Better Auth may return 200 even if user doesn't exist (security)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Request failed');
  }

  return { message: 'If an account exists with that email, a reset link has been sent.' };
}

/**
 * Reset password with token from email
 * Better Auth endpoint: POST /api/auth/reset-password
 */
export async function resetPassword(token: string, password: string): Promise<{ message: string }> {
  const root = getRootApiUrl();
  const res = await fetch(`${root}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, newPassword: password }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Password reset failed');
  }

  return { message: 'Password reset successful. You can now login.' };
}

// ==================== Session ====================

/**
 * Get current session and user
 * Better Auth endpoint: GET /api/auth/session
 */
export async function getSession(): Promise<Session | null> {
  const root = getRootApiUrl();
  const res = await fetch(`${root}/api/auth/session`, {
    credentials: 'include',
  });

  if (!res.ok) {
    return null;
  }

  return res.json();
}

/**
 * Get current authenticated user (wrapper for getSession)
 * Uses our custom /api/auth/me endpoint for extended user data
 */
export async function getCurrentUser(): Promise<User | null> {
  const root = getRootApiUrl();
  const meUrl = `${root}/api/auth/me`;
  const res = await fetch(meUrl, {
    credentials: 'include',
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    return null;
  }

  return res.json();
}

// ==================== OAuth ====================

/**
 * Start OAuth sign-in and return the provider authorization URL.
 *
 * Better Auth endpoint: POST /api/auth/sign-in/social
 *
 * Retries up to OAUTH_MAX_RETRIES times on 5xx or network errors to handle
 * API cold-start scenarios where the server needs a moment to wake up.
 */
export async function getSocialOAuthRedirectUrl(
  provider: 'google' | 'github',
): Promise<string> {
  const root = getRootApiUrl();

  let lastError: unknown;

  for (let attempt = 0; attempt <= OAUTH_MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(`${root}/api/auth/sign-in/social`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        redirect: 'manual',
        body: JSON.stringify({ provider, callbackURL: `${window.location.origin}/auth/${provider}/callback` }),
        signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
      });

      if (res.ok) {
        const data = (await res.json().catch(() => null)) as null | { url?: string };
        const url = data?.url || res.headers.get('location');
        if (!url) {
          throw new Error(`OAuth response missing redirect url for provider: ${provider}`);
        }
        return url;
      }

      // 5xx: server error — retryable (cold start, transient failure)
      if (res.status >= 500 && attempt < OAUTH_MAX_RETRIES) {
        const delay = OAUTH_RETRY_DELAYS_MS[attempt];
        await new Promise<void>((r) => setTimeout(r, delay));
        continue;
      }

      // 4xx or final attempt: non-retryable
      const body = await res.text().catch(() => '');
      throw new Error(
        `Failed to start ${provider} OAuth (${res.status})${body ? `: ${body}` : ''}`,
      );
    } catch (err) {
      // Network-level error (no HTTP response) — retryable
      if (isFetchNetworkError(err) && attempt < OAUTH_MAX_RETRIES) {
        lastError = err;
        const delay = OAUTH_RETRY_DELAYS_MS[attempt];
        await new Promise<void>((r) => setTimeout(r, delay));
        continue;
      }
      throw err;
    }
  }

  // Exhausted retries after network errors — re-throw the last error
  throw lastError;
}

// ==================== User Profile ====================

/**
 * Update user profile (name, avatar image)
 * Better Auth endpoint: POST /api/auth/update-user
 */
export async function updateUser(data: UpdateUserRequest): Promise<User> {
  const root = getRootApiUrl();
  const res = await fetch(`${root}/api/auth/update-user`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update profile');
  }

  return res.json();
}

/**
 * Upload an avatar image file
 * Custom endpoint: POST /api/auth/avatar/upload
 * Returns the new avatar URL on success.
 */
export async function uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
  const root = getRootApiUrl();
  const form = new FormData();
  form.append('avatar', file);

  const res = await fetch(`${root}/api/auth/avatar/upload`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to upload avatar');
  }

  return res.json();
}

/**
 * Change password for authenticated user
 * Better Auth endpoint: POST /api/auth/change-password
 */
export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  const root = getRootApiUrl();
  const res = await fetch(`${root}/api/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to change password');
  }
}

/**
 * Verify email with token
 * Better Auth endpoint: POST /api/auth/verify-email
 */
export async function verifyEmail(token: string): Promise<{ message: string }> {
  const root = getRootApiUrl();
  const res = await fetch(`${root}/api/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Email verification failed');
  }

  return res.json();
}

// ==================== Account Management ====================

export interface ConnectedAccount {
  provider: 'google' | 'github' | 'credential';
  connectedAt: string;
}

export interface AccountsResponse {
  accounts: ConnectedAccount[];
}

/**
 * List connected sign-in methods for the current user.
 */
export async function getAccounts(): Promise<AccountsResponse> {
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/me/accounts`, {
    credentials: 'include',
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to fetch accounts');
  }

  return res.json();
}

/**
 * Disconnect a sign-in provider.
 * DELETE /api/v1/me/accounts/:provider
 */
export async function disconnectAccount(provider: string): Promise<void> {
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/me/accounts/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
    credentials: 'include',
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to disconnect account');
  }
}

/**
 * Set a password for an OAuth-only account (no current password required).
 * POST /api/v1/me/accounts/password { password }
 */
export async function setPassword(password: string): Promise<void> {
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/me/accounts/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ password }),
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to set password');
  }
}

/**
 * Start linking a social provider to the current account.
 * Returns a redirect URL to send the user to.
 * POST /api/auth/link-social { provider, callbackURL }
 */
export async function linkSocialAccount(
  provider: 'google' | 'github',
  callbackURL: string,
): Promise<string> {
  const root = getRootApiUrl();
  const res = await fetch(`${root}/api/auth/link-social`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ provider, callbackURL }),
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Failed to start ${provider} account linking`);
  }

  const data = (await res.json()) as { url?: string };
  if (!data.url) {
    throw new Error(`Link social response missing redirect url for provider: ${provider}`);
  }
  return data.url;
}

/**
 * Check a reset token — returns whether the token owner has a password.
 * GET /api/auth/reset-token-info?token=...
 */
export async function getResetTokenInfo(
  token: string,
): Promise<{ valid: boolean; hasPassword?: boolean; email?: string }> {
  const root = getRootApiUrl();
  try {
    const res = await fetch(
      `${root}/api/auth/reset-token-info?token=${encodeURIComponent(token)}`,
      {
        credentials: 'include',
        signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
      },
    );

    if (!res.ok) {
      return { valid: false };
    }

    return res.json();
  } catch {
    return { valid: false };
  }
}

/**
 * Set a password using a reset token (unauthenticated — for OAuth-only forgot-password flow).
 * POST /api/v1/auth/set-password-with-token { token, password }
 */
export async function setPasswordWithToken(
  token: string,
  password: string,
): Promise<void> {
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/auth/set-password-with-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, password }),
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to set password with token');
  }
}

/**
 * Dismiss the account-link merge notice banner.
 * POST /api/v1/me/dismiss-merge-notice
 */
export async function dismissMergeNotice(): Promise<void> {
  const apiBase = getApiBase();
  const res = await fetch(`${apiBase}/me/dismiss-merge-notice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: '{}',
    signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to dismiss merge notice');
  }
}

/**
 * Fetch current user directly from session (to check pendingMergeNotice without cache delay).
 * Returns null if not authenticated.
 * GET /api/auth/get-session
 */
export async function getMe(): Promise<(User & { pendingMergeNotice?: boolean }) | null> {
  const root = getRootApiUrl();
  try {
    const res = await fetch(`${root}/api/auth/get-session`, {
      credentials: 'include',
      signal: AbortSignal.timeout(AUTH_FETCH_TIMEOUT_MS),
    });

    if (!res.ok) {
      return null;
    }

    const data = (await res.json()) as { user?: User & { pendingMergeNotice?: boolean } };
    return data.user ?? null;
  } catch {
    return null;
  }
}
